import React, { useEffect, useRef, forwardRef } from "react";
import PropTypes from "prop-types";
import { overrideTailwindClasses } from "tailwind-override";
import Close from "images/close.svg";
import ReactDOM from "react-dom";

/**
 * @type {ForwardedRef}
 */
const Modal = forwardRef(
  (
    {
      className = "",
      modalBackgroundClassName = "",
      modalContainerClassName = "",
      children,
      isCloseHidden = false,
      isOpen = false,
      onClose = () => {},
      dataTestId = "modal",
      leftButton = null, // Set automatically in ModalWithNavigation component
      rightButton = null, // Set automatically in ModalWithNavigation component
    },
    ref
  ) => {
    const internalRef = useRef(null);
    const modalRef = ref || internalRef;
    const leftButtonRef = useRef(null);
    const rightButtonRef = useRef(null);

    useEffect(() => {
      function handleClickOutside(event) {
        const isOutsideModal = modalRef.current && !modalRef.current.contains(event.target);
        const isNotLeftButton = !leftButtonRef.current || !leftButtonRef.current.contains(event.target);
        const isNotRightButton = !rightButtonRef.current || !rightButtonRef.current.contains(event.target);

        if (isOutsideModal && isNotLeftButton && isNotRightButton) {
          onClose();
        }
      }

      if (isOpen) {
        document.addEventListener("mousedown", handleClickOutside);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isOpen, onClose]);

    useEffect(() => {
      function handleKeyDown(event) {
        if (event.key === "Escape") {
          onClose();
        }
      }

      if (isOpen) {
        document.addEventListener("keydown", handleKeyDown);
      }

      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return ReactDOM.createPortal(
      <div
        data-test-id="modal-background"
        className={overrideTailwindClasses(
          `fixed w-screen h-screen flex justify-center items-center bg-black bg-opacity-50 z-modal top-0 cursor-pointer ${modalBackgroundClassName}`
        )}
        role="button"
        aria-label="Close modal"
        tabIndex="0">
        <div
          data-test-id="modal-container"
          className={overrideTailwindClasses(
            `flex items-center w-full h-full overflow-auto sm:w-3/4 sm:h-3/4 sm:overflow-hidden cursor-auto ${modalContainerClassName}`
          )}>
          {leftButton && React.cloneElement(leftButton, { ref: leftButtonRef })}
          <div
            data-test-id={dataTestId}
            ref={modalRef}
            className={overrideTailwindClasses(`bg-white w-full h-full sm:relative ${className}`)}>
            {children}
            {!isCloseHidden && (
              <button
                data-test-id="modal-close-button"
                aria-label="Close modal"
                className="absolute top-4 right-4 z-modal"
                onClick={onClose}>
                <Close className="border border-offWhite bg-white bg-opacity-80 rounded-full sm:border-none sm:bg-none" />
              </button>
            )}
          </div>
          {rightButton && React.cloneElement(rightButton, { ref: rightButtonRef })}
        </div>
      </div>,
      document.body
    );
  }
);

Modal.displayName = "Modal";

Modal.propTypes = {
  modalBackgroundClassName: PropTypes.string,
  modalContainerClassName: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
  isOpen: PropTypes.bool,
  isCloseHidden: PropTypes.bool,
  onClose: PropTypes.func,
  dataTestId: PropTypes.string,
  leftButton: PropTypes.node,
  rightButton: PropTypes.node,
};

export default Modal;
